// How the parlour app reaches the api (decision BD-27), as the console does. The app is served
// from app.{domain} and calls api.{domain} with credentials: both are the same site, so the
// api's SameSite=Lax, HttpOnly staff session cookie (S-007) is sent, and the api's CORS
// allow-list lets this origin read answers. In development set VITE_API_URL.

export function apiBase(loc: Pick<Location, "protocol" | "hostname"> = window.location): string {
  const fromEnv = import.meta.env?.VITE_API_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  return `${loc.protocol}//${loc.hostname.replace(/^app\./, "api.")}`;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  rule: string;
  fields: Record<string, string>;
}

export type ApiResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; error?: ApiErrorBody };

async function answer<T>(res: Response): Promise<ApiResult<T>> {
  const json = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (res.ok) return { ok: true, status: res.status, data: json as T };
  return { ok: false, status: res.status, error: (json as { error?: ApiErrorBody } | undefined)?.error };
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; headers?: Record<string, string> } = {},
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method: init.method ?? "GET",
      credentials: "include",
      headers: { ...(init.body === undefined ? {} : { "Content-Type": "application/json" }), ...init.headers },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    return { ok: false, status: 0 };
  }
  return answer<T>(res);
}

/** Sends a file's bytes as the body (S-029 upload endpoints decide the type from the content). */
export async function uploadFile<T>(path: string, file: Blob): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
    });
  } catch {
    return { ok: false, status: 0 };
  }
  return answer<T>(res);
}
